import type { ButtonHTMLAttributes, FC, ReactNode } from 'react';

import { MuiCustomButton } from './MuiButton.style';

import type { ButtonProps } from '@mui/material';

type ButtonVariant = 'outlined' | 'contained' | 'text' | 'social';
export type MuiButtonProps = {
  variant?: ButtonVariant;
  isDisabled?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  label?: string;
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  isStopPropagation?: boolean;
  fullWidth?: boolean;
  fillStartIcon?: string;
  fillEndIcon?: string;
} & Partial<Omit<ButtonProps, 'variant'>>;

export const buttonVariant: {
  [Key in ButtonVariant]: {
    className: string;
    variant: Exclude<ButtonVariant, 'social'>;
  };
} = {
  contained: {
    className: '',
    variant: 'contained',
  },
  outlined: {
    className: '',
    variant: 'outlined',
  },
  text: {
    className: '',
    variant: 'text',
  },
  social: {
    className: 'outline-social-button',
    variant: 'outlined',
  },
};

export const MuiButton: FC<MuiButtonProps> = ({
  variant = 'outlined',
  size = 'large',
  color,
  isDisabled,
  startIcon,
  endIcon,
  label,
  isStopPropagation = true,
  type = 'button',
  onClick,
  fullWidth = false,
  fillStartIcon,
  fillEndIcon,
  ...args
}) => {
  const selectVariant = buttonVariant[variant || 'outlined']!;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (isStopPropagation) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (onClick) {
      onClick(e);
    }
  };

  return (
    <MuiCustomButton
      {...args}
      className={`${selectVariant.className} ${label ? '' : 'btn-without-label'}`}
      classes={{ root: 'button-root' }}
      variant={selectVariant.variant}
      color={color}
      size={size}
      disabled={isDisabled}
      startIcon={startIcon}
      endIcon={endIcon}
      onClick={handleClick}
      type={type}
      fillStartIcon={fillStartIcon}
      fillEndIcon={fillEndIcon}
      fullWidth={fullWidth}
    >
      <span>{label}</span>
    </MuiCustomButton>
  );
};
